import { useApiMutation, useApiQuery } from "@/lib/query";
import type {
  AddedItems,
  AddItems,
  CompleteItem,
  HumanScores,
  Queue,
  QueueCreate,
  QueueDetail,
  QueueUpdate,
  ScoreConfig,
  ScoreConfigCreate,
} from "./types";

const CONFIGS = "/score-configs";
const QUEUES = "/annotation-queues";

export const useScoreConfigs = () => useApiQuery<ScoreConfig[]>(CONFIGS);

export const useCreateScoreConfig = () =>
  useApiMutation<ScoreConfig, ScoreConfigCreate>("post", CONFIGS, { invalidate: [CONFIGS] });

export const useQueues = () => useApiQuery<Queue[]>(QUEUES);

/** A 404 is the answer for a queue that was not shared with me: no retry. */
export const useQueue = (id: string) => useApiQuery<QueueDetail>(`${QUEUES}/${id}`, {}, { retry: false });

export const useCreateQueue = () =>
  useApiMutation<Queue, QueueCreate>("post", QUEUES, { invalidate: [[QUEUES]] });

export const useUpdateQueue = (id: string) =>
  useApiMutation<QueueDetail, QueueUpdate>("patch", `${QUEUES}/${id}`, { invalidate: [QUEUES] });

/** Answers show on the experiment pages too, so their human scores refetch. */
const HUMAN_SCORES = /\/human-scores$/;

export const useAddItems = () =>
  useApiMutation<AddedItems, AddItems & { queueId: string }>(
    "post",
    ({ queueId }) => `${QUEUES}/${queueId}/items`,
    { invalidate: [QUEUES], body: ({ queueId: _, ...body }) => body },
  );

export const useCompleteItem = (queueId: string) =>
  useApiMutation<void, CompleteItem & { itemId: string }>(
    "post",
    ({ itemId }) => `${QUEUES}/${queueId}/items/${itemId}/complete`,
    { invalidate: [QUEUES, HUMAN_SCORES], body: ({ itemId: _, ...body }) => body },
  );

export const useHumanScores = (experimentId: string) =>
  useApiQuery<HumanScores>(`/experiments/${experimentId}/human-scores`);
